import { ICustomRoute } from './baseTypes';
import { UAMPermissions } from './userTypes';

export enum ROUTES {
  HOME = '/',
  NOTFOUND = '/404',
  FEED = '/activity',

  MENU = '/account',
  LOGIN = '/login',
  REGISTER = '/login/register',
  SIGNUPCREATOR = '/login/signup',
  VERIFY_OTP = '/login/verify-otp',
  VERIFY_EMAIL_PHONE = '/login/verify-email-phone',
  REFER_N_EARN_SIGNUP = '/sign-up',
  POST_DETAILS = '/post/:postId',
  VIDEO_CALL = '/video-call',
  CREATOR_PROFILE = '/creator/profile/:id',
  CREATOR_PROFILE_SLUG = '/creator/:slug',
  NOTIFICATIONS = '/notifications',
  PERSONAL_INFO = '/account/personal-information',
  ABOUT_YOU = '/account/about',
  SUBSCRIPTIONS = '/account/subscriptions',
  CHANGE_NUMBER = '/account/change-number',
  CHANGE_EMAIL = '/account/change-email',
  DELETE_ACCOUNT = '/account/delete',
  ACCOUNT_SETTINGS = '/account/settings',
  HELP_SUPPORT = '/account/help-support',
  VERIFY_OTP_ACCOUNT = '/account/verify-otp',
  NOTIFICATION_SETTINGS = '/account/notification-settings',
  VERIFY_EMAIL = '/verify-email',
  STOREFRONT = '/storefront',
  USER_PROFILE = '/profile/:userId',
  MANGOES_PURCHASED = '/mangoes-purchased/:userId',

  CREATE_POST = '/create-post',
  CREATE_POLL = '/create-poll',

  //Courses
  COURSES = '/courses',
  COURSE_DETAILS = '/courses/:courseId',
  COURSE_OVERVIEW = '/courses/details/:courseId',

  //Messages
  MESSAGES = '/messages',
  NEW_MESSAGE = '/messages/new',
  MESSAGE_ROOM = '/messages/room/:roomId',
  MESSAGE_DM = '/messages/dm/:roomId',
  MESSAGE_DM_PARTICIPANT = '/messages/participant/:otherParticipantId',

  //Custom Apps
  CUSTOM_APPS = '/apps/:key',

  // Gamification
  LEADERBOARD = '/leaderboard',
  POINTS_LEDGER = '/points',

  // MetaData View Routes
  POST_METADATA_VIEW = '/lite/view/post/:postId',
  WEBINAR_METADATA_VIEW = '/lite/view/webinar/:webinarId',
  CHAPTER_METADATA_VIEW = '/lite/view/chapter/:chapterId',
  CHANNEL_POST_METADATA_VIEW = '/lite/view/channel/:channelId/post/:postId',

  // Community
  COMMUNITY = '/community',
  COMMUNITY_FEED = '/community/feed',
  COMMUNITY_MESSAGES = '/community/messages',
  COMMUNITY_TAGMANGO_TEAM = '/community/tagmango-team',
  COMMUNITY_CHANNEL = '/community/channel/:channelId',
  COMMUNITY_CREATE_POST = '/community/create-post',
  COMMUNITY_CREATE_POLL = '/community/create-poll',
  COMMUNITY_POST_DETAILS = '/community/channel/:channelId/post/:postId',
  COMMUNITY_NEW_MESSAGES = '/community/new-messages',
  COMMUNITY_MESSAGES_ROOM = '/community/messages/chat-room/:roomId',

  // TQ Connect Routes
  TQ_CONNECT = '/community/tq-connect',
  TQ_CONNECT_SERVICE_REQUESTS = '/community/tq-connect/service-requests',
  TQ_CONNECT_SERVICE_REQUESTS_DISCOVER = '/community/tq-connect/service-requests/discover', // opens in Community -> Service Requests -> Discover
  TQ_CONNECT_SERVICE_REQUESTS_MY_ACTIVITY = '/community/tq-connect/service-requests/my-activity', // opens in Community -> Service Requests -> My Activity
  TQ_CONNECT_SERVICE_REQUESTS_DETAILS = '/community/tq-connect/service-requests/details/:serviceRequestId', // opens in Community -> Service Requests -> Details
  TQ_CONNECT_TOP_CONTRIBUTORS = '/community/tq-connect/top-contributors',
  TQ_CONNECT_SERVICE_REQUESTS_CREATE = '/community/tq-connect/service-requests/create',
  TQ_CONNECT_PROFILE_COMPLETE_QUESTIONS = '/community/tq-connect/profile-complete-questions',
  TQ_CONNECT_HELP_OFFER_FORM = '/community/tq-connect/help-offer-form/:serviceRequestId',
  TQ_CONNECT_RECENT_ACTIVITY = '/community/tq-connect/recent-activity/:requestType',
  TQ_CONNECT_SHOUTOUTS = '/community/tq-connect/shoutouts',
  TQ_CONNECT_SHOUTOUT_CREATE = '/community/tq-connect/shoutouts/create',
  TQ_CONNECT_SERVICE_MARK_AS_COMPLETE = '/community/tq-connect/service-mark-as-complete/:serviceRequestId',
  TQ_CONNECT_SERVICE_REQUESTS_RESPONSE_DETAILS = '/community/tq-connect/service-requests/:serviceRequestId/response',

  // Other routes
  PLATFORM_CRASHED = '/platform-crashed',
  NO_ACCESS = '/no-access',
}

// Community Routes
export enum COMMUNITY_ROUTES {
  TAGMANGO_TEAM = '/tagmango-team',
  FEED = '/feed',
  MESSAGES = '/messages',
  CHANNEL = '/channel/:channelId',
  TQ_CONNECT_SERVICE_REQUESTS_DISCOVER = '/tq-connect/service-requests/discover',
  TQ_CONNECT_SERVICE_REQUESTS_MY_ACTIVITY = '/tq-connect/service-requests/my-activity',
  TQ_CONNECT_SERVICE_REQUESTS_DETAILS = '/tq-connect/service-requests/details/:serviceRequestId',
  TQ_CONNECT_PROFILE_COMPLETE_QUESTIONS = '/tq-connect/profile-complete-questions',
  TQ_CONNECT_HELP_OFFER_FORM = '/tq-connect/help-offer-form/:serviceRequestId',
}

// Community Routes Keys
export const COMMUNITY_ROUTES_KEYS: Record<COMMUNITY_ROUTES, string> = {
  [COMMUNITY_ROUTES.TAGMANGO_TEAM]: 'tagmango-team',
  [COMMUNITY_ROUTES.FEED]: 'feed',
  [COMMUNITY_ROUTES.MESSAGES]: 'messages',
  [COMMUNITY_ROUTES.CHANNEL]: 'channel',
  [COMMUNITY_ROUTES.TQ_CONNECT_SERVICE_REQUESTS_DISCOVER]: 'discover',
  [COMMUNITY_ROUTES.TQ_CONNECT_SERVICE_REQUESTS_MY_ACTIVITY]: 'my-activity',
  [COMMUNITY_ROUTES.TQ_CONNECT_SERVICE_REQUESTS_DETAILS]: 'details',
  [COMMUNITY_ROUTES.TQ_CONNECT_PROFILE_COMPLETE_QUESTIONS]:
    'profile-complete-questions',
  [COMMUNITY_ROUTES.TQ_CONNECT_HELP_OFFER_FORM]:
    'help-offer-form/:serviceRequestId',
};

// Tab Routes
export enum RouteQuery {
  POST_ID = 'postId',
  CHAPTER_ID = 'chapterId',
  WEBINAR_ID = 'webinarId',
}

export type HomeRoutes =
  | ROUTES.FEED
  | ROUTES.COURSES
  | ROUTES.MESSAGES
  | ROUTES.VIDEO_CALL
  | ROUTES.CUSTOM_APPS
  | ROUTES.LEADERBOARD
  | ROUTES.STOREFRONT
  | ROUTES.COMMUNITY;

export const PATH_MAP: Record<string, HomeRoutes> = {
  '/activity': ROUTES.FEED,
  '/courses': ROUTES.COURSES,
  '/messages': ROUTES.MESSAGES,
  '/dashboard': ROUTES.FEED,
  '/video-call': ROUTES.VIDEO_CALL,
  '/leaderboard': ROUTES.LEADERBOARD,
  '/storefront': ROUTES.STOREFRONT,
  '/community': ROUTES.COMMUNITY,
};

export const UAM_PATH_ACCESS_MAP: Partial<Record<HomeRoutes, UAMPermissions>> =
  {
    [ROUTES.FEED]: UAMPermissions.FEED_READ,
    [ROUTES.COURSES]: UAMPermissions.COURSE_ACCESS,
    [ROUTES.MESSAGES]: UAMPermissions.MESSAGES_ACCESS,
    [ROUTES.VIDEO_CALL]: UAMPermissions.DASHBOARD_WORKSHOP_ACCESS,
    [ROUTES.LEADERBOARD]: UAMPermissions.DASHBOARD_GAMIFICATION_ACCESS,
    [ROUTES.STOREFRONT]: UAMPermissions.DASHBOARD_STOREFRONT_ACCESS,
    [ROUTES.CUSTOM_APPS]: UAMPermissions.CUSTOM_APP_ACCESS,
    [ROUTES.COMMUNITY]: UAMPermissions.COMMUNITY_ACCESS,
  };

export const ROUTES_TABS_MAP: Partial<Record<HomeRoutes, ICustomRoute>> = {
  [ROUTES.FEED]: {
    name: ROUTES.FEED,
    config: {
      title: 'Feed',
      path: ROUTES.FEED,
      key: 'feed',
    },
  },
  [ROUTES.COURSES]: {
    name: ROUTES.COURSES,
    config: {
      title: 'Courses',
      path: ROUTES.COURSES,
      key: 'courses',
    },
  },
  [ROUTES.MESSAGES]: {
    name: ROUTES.MESSAGES,
    config: {
      title: 'Messages',
      path: ROUTES.MESSAGES,
      key: 'messages',
    },
  },
  [ROUTES.VIDEO_CALL]: {
    name: ROUTES.VIDEO_CALL,
    config: {
      title: 'Workshops',
      path: ROUTES.VIDEO_CALL,
      key: 'webinars',
    },
  },
  [ROUTES.STOREFRONT]: {
    name: ROUTES.STOREFRONT,
    config: {
      title: 'Storefront',
      path: ROUTES.STOREFRONT,
      key: 'storefront',
      icon: 'storefront',
    },
  },
  [ROUTES.COMMUNITY]: {
    name: ROUTES.COMMUNITY,
    config: {
      title: 'Community',
      path: ROUTES.COMMUNITY,
      key: 'community',
      icon: 'people',
    },
  },
};

export enum TQConnectSetupTabs {
  TRUST_TRIGGERS = 'trust-triggers',
  TRUST_LEVELS = 'trust-levels',
}

export enum UserProfileTabs {
  ABOUT = 'about',
  POSTS = 'posts',
  PURCHASES = 'purchases',
  REMARKS = 'remarks',
  REVIEWS = 'reviews',
  SHOUTOUTS = 'shoutouts',
}

export enum TQConnectServiceRequestsMyActivityTabs {
  REQUEST_RAISED = 'request-raised',
  REQUEST_RECEIVED = 'request-received',
  RESPONDED = 'responded',
}
